import { useRef, useState, useMemo, useCallback } from 'react';
import {
    DEFAULT_HUB_CONNECTION_URL,
    FINISHED_STATUSES,
    INITIAL_STATUS,
    SUCCESS_STATUS,
    SUCCESS_WARNING_STATUS,
} from 'constants/uploadDownload';
import { useIntl } from 'react-intl';
import { fileUploadRequest, getHumanFileSize } from 'services/items';
import useExceptionToast from './useExceptionToast';
import { useMsal } from '@azure/msal-react';
import { scopes } from 'services/auth';
import { HubConnectionBuilder } from '@microsoft/signalr';

export const useFileUpload = (onAfterUploadChange, hubConnectionUrl = DEFAULT_HUB_CONNECTION_URL) => {
    const intl = useIntl();
    const hubConnection = useRef(null);
    const { instance } = useMsal();
    const reqAbortController = useRef(null);
    const [percentage, setPercentage] = useState(0);
    const [file, setFile] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [fileUploadProgress, setFileUploadProgress] = useState(0);
    const [error, setError] = useState('');
    const { exceptionToast } = useExceptionToast();

    const [phase, setPhase] = useState({
        status: INITIAL_STATUS,
        title: intl.formatMessage({ id: 'upload_modal_initial_stage_title' }),
    });

    const onReceiveStatusUpdate = (statusData) => {
        setPhase(statusData);
        setPercentage(0);

        if (
            (statusData.status === SUCCESS_WARNING_STATUS || statusData.status === SUCCESS_STATUS) &&
            onAfterUploadChange
        ) {
            onAfterUploadChange();
        }

        if (FINISHED_STATUSES.includes(statusData.status)) {
            if (hubConnection.current) {
                hubConnection.current.stop();
                hubConnection.current = null;
            }
            setIsLoading(false);
            setPercentage(0);
            setFileUploadProgress(0);
        }
    };

    const onReceivePercentageUpdate = (percentage) => {
        setPercentage(percentage);
    };

    const closeHubConnection = useCallback(() => {
        setIsLoading(false);
        setPercentage(0);
        setFileUploadProgress(0);
        reqAbortController.current?.abort();
        hubConnection.current?.stop();
        hubConnection.current = null;
    }, [hubConnection, reqAbortController]);

    const createNewHubConnection = async () => {
        const token = await instance.acquireTokenSilent({ scopes: scopes });
        const conn = new HubConnectionBuilder()
            .withUrl(hubConnectionUrl, {
                accessTokenFactory: async () => {
                    return token?.accessToken;
                },
            })
            .build();

        conn.on('ReceiveStatusUpdate', onReceiveStatusUpdate);
        conn.on('ReceivePercentageUpdate', onReceivePercentageUpdate);
        await conn.start();
        hubConnection.current = conn;
        return conn.connectionId;
    };

    const cancelUpload = () => {
        if (reqAbortController.current) {
            reqAbortController.current?.abort();
        }
    };

    const handleFileChange = (file) => {
        const formattedFile = new File([file], file.name, { type: file.type });
        formattedFile['friendlySize'] = getHumanFileSize(file.size, true, 2);

        setFile(formattedFile);
    };

    const startUpload = async (route, queryParams = {}, headers = {}) => {
        if (!file) return;

        setError('');
        setIsLoading(true);

        if (reqAbortController.current) {
            reqAbortController.current?.abort();
            closeHubConnection();
        }

        const abortController = new AbortController();
        reqAbortController.current = abortController;
        const signal = abortController.signal;

        try {
            const sseClientId = await createNewHubConnection();

            const config = {
                onUploadProgress: (progressEvent) => {
                    setFileUploadProgress(progressEvent.loaded);
                    setPercentage((progressEvent.loaded / file.size) * 100);
                },
                signal: signal,
            };

            const params = new URLSearchParams(queryParams);
            params.append('sseClientId', sseClientId);

            await fileUploadRequest(route, file, params, headers, config);
        } catch (err) {
            if (err?.code === 'ERR_CANCELED') {
                setError(intl.formatMessage({ id: 'upload_request_canceled_message' }));
                closeHubConnection();
            } else {
                const errorMessage =
                    err.response?.data?.error || intl.formatMessage({ id: 'upload_modal_failed_import' });
                setError(errorMessage);
                const validationException =
                    err.response?.data?.details &&
                    err.response?.data?.details?.toUpperCase().includes('VALIDATIONEXCEPTION');

                if (validationException) {
                    const toastErrorDetails = err.response?.data?.details;
                    exceptionToast(intl.formatMessage({ id: 'upload_modal_validation_exception' }), toastErrorDetails);
                }
            }
        } finally {
            setIsLoading(false);
            reqAbortController.current = null;
        }
    };

    const removeFile = () => {
        setFile(null);
    };

    const friendlyUploadProgress = useMemo(() => {
        return getHumanFileSize(fileUploadProgress, true, 2);
    }, [fileUploadProgress]);

    return {
        removeFile,
        cancelUpload,
        startUpload,
        handleFileChange,
        closeHubConnection,
        file,
        error,
        phase,
        isLoading,
        percentage,
        fileUploadProgress,
        friendlyUploadProgress,
    };
};
